/**
 * Trees and plants: twelve species in a chunky cartoon style. Each species is
 * drawn around its own base point (0, 0) with "up" = −y, so the painter can
 * place, lean and sway it with one transform.
 *
 * Trees are planned deterministically from the layer seed, so the static and
 * swaying passes (and every frame) agree on where each tree stands.
 */
import type { TreeSpecies, TreesLayer } from '@dropstack/core';
import { spreadPositions, type SceneSize } from '../layout.ts';
import { hash01, range, rngFor } from '../noise.ts';
import {
  bodyGradient,
  circleBlob,
  circlePath,
  ellipseBlob,
  ellipsePath,
  gloss,
  leafPath,
  quadPoint,
  ribbonPath,
  rgba,
  rimOf,
  shade,
  strokeRim,
  vGradient,
  type Circle,
  type Ctx,
  type Point,
} from './draw.ts';
import type { PaintEnv, Painter } from './types.ts';

/** One tree, as its species function sees it. */
interface Tree {
  /** Height in CSS px. */
  h: number;
  color: string;
  trunk: string;
  accent: string | undefined;
  size: SceneSize;
  /** Per-tree seed for small shape variations. */
  seed: number;
  /** Scene seconds (0 for trees that don't sway). */
  time: number;
  phase: number;
  /** +1: asymmetric features (lean, long branches) point right; −1: left. */
  flip: 1 | -1;
}

interface TreePlan {
  x: number;
  y: number;
  h: number;
  seed: number;
  phase: number;
  flip: 1 | -1;
}

function planTrees(layer: TreesLayer, env: PaintEnv): TreePlan[] {
  const { size, seed } = env;
  const rng = rngFor(seed, 1);
  const xs = spreadPositions(layer.count, layer.spread ?? [-0.05, 1.05], (i) => hash01(seed + 3, i));
  const plans = xs.map((fx, i): TreePlan => {
    const x = fx * size.width;
    // Lean towards the middle of the screen so trees frame the playfield.
    const towardsCentre: 1 | -1 = x < size.width / 2 ? 1 : -1;
    return {
      x,
      y: layer.baseline * size.height + size.unit * range(rng, -0.008, 0.008),
      h: layer.size * size.unit * range(rng, 0.82, 1.18),
      seed: (seed ^ Math.imul(i + 1, 0x9e3779b1)) >>> 0,
      phase: range(rng, 0, Math.PI * 2),
      flip: hash01(seed + 5, i) < 0.8 ? towardsCentre : (-towardsCentre as 1 | -1),
    };
  });
  // Trees standing higher up the slope are further away: paint them first.
  return plans.sort((a, b) => a.y - b.y);
}

// ───────────── shared parts ─────────────

/** Foliage clump: one outlined blob, lighter lobes on top and a glossy highlight. */
function foliage(ctx: Ctx, circles: readonly Circle[], color: string, t: Tree): void {
  if (circles.length === 0) return;
  const top = Math.min(...circles.map((c) => c.y - c.r));
  const bottom = Math.max(...circles.map((c) => c.y + c.r));
  circleBlob(ctx, circles, bodyGradient(ctx, color, top, bottom, 0.16, -0.14), rimOf(color), t.size);
  const mid = (top + bottom) / 2;
  ctx.beginPath();
  for (const c of circles) if (c.y < mid) circlePath(ctx, c.x - c.r * 0.2, c.y - c.r * 0.22, c.r * 0.58);
  ctx.fillStyle = rgba(shade(color, 0.24), 0.5);
  ctx.fill();
  const lead = circles.reduce((best, c) => (c.y - c.r < best.y - best.r ? c : best));
  gloss(ctx, lead.x - lead.r * 0.35, lead.y - lead.r * 0.35, lead.r * 0.34, lead.r * 0.17, 0.4);
}

/** A tapered trunk or branch along a quadratic curve, with a cartoon rim and a soft side sheen. */
function limb(ctx: Ctx, from: Point, ctrl: Point, to: Point, w0: number, w1: number, color: string, t: Tree, sheen = true): void {
  const pts = Array.from({ length: 9 }, (_, i) => quadPoint(from, ctrl, to, i / 8));
  ctx.beginPath();
  ribbonPath(ctx, pts, (s) => w0 + (w1 - w0) * s);
  ctx.fillStyle = vGradient(ctx, to.y, from.y, [shade(color, 0.1), color, shade(color, -0.08)]);
  ctx.fill();
  strokeRim(ctx, rimOf(color), t.size);
  if (!sheen) return;
  ctx.beginPath();
  const side = pts.map((p, i) => ({ x: p.x - (w0 + (w1 - w0) * (i / 8)) * 0.22, y: p.y }));
  ctx.moveTo(side[1]!.x, side[1]!.y);
  for (const p of side.slice(2, -1)) ctx.lineTo(p.x, p.y);
  ctx.strokeStyle = rgba(shade(color, 0.35), 0.45);
  ctx.lineWidth = Math.max(1, w1 * 0.25);
  ctx.lineCap = 'round';
  ctx.stroke();
}

/** Small five-petal flower (blossoms, jungle flowers). */
function tinyFlowers(ctx: Ctx, at: readonly Point[], r: number, color: string): void {
  ctx.beginPath();
  for (const p of at) {
    for (let k = 0; k < 5; k++) {
      const a = (k / 5) * Math.PI * 2;
      circlePath(ctx, p.x + Math.cos(a) * r * 0.6, p.y + Math.sin(a) * r * 0.6, r * 0.5);
    }
  }
  ctx.fillStyle = color;
  ctx.fill();
  ctx.beginPath();
  for (const p of at) circlePath(ctx, p.x, p.y, r * 0.35);
  ctx.fillStyle = '#ffe066';
  ctx.fill();
}

/** Points scattered inside a set of circles (fruit, blossoms), deterministic per tree. */
function pointsIn(circles: readonly Circle[], n: number, seed: number): Point[] {
  const rng = rngFor(seed, 77);
  return Array.from({ length: n }, (_, i) => {
    const c = circles[i % circles.length]!;
    const a = range(rng, 0, Math.PI * 2);
    const d = Math.sqrt(rng.next()) * c.r * 0.72;
    return { x: c.x + Math.cos(a) * d, y: c.y + Math.sin(a) * d };
  });
}

// ───────────── species ─────────────

function roundTree(ctx: Ctx, t: Tree): void {
  const { h } = t;
  limb(ctx, { x: 0, y: 0 }, { x: 0, y: -h * 0.3 }, { x: t.flip * h * 0.02, y: -h * 0.6 }, h * 0.13, h * 0.07, t.trunk, t);
  const rng = rngFor(t.seed, 5);
  const R = h * 0.3;
  const cy = -h * 0.66;
  const circles: Circle[] = [
    { x: 0, y: cy, r: R * 0.95 },
    { x: -R * 0.74, y: cy + R * 0.28, r: R * range(rng, 0.58, 0.68) },
    { x: R * 0.74, y: cy + R * 0.25, r: R * range(rng, 0.58, 0.7) },
    { x: -R * 0.42, y: cy - R * 0.5, r: R * range(rng, 0.55, 0.65) },
    { x: R * 0.45, y: cy - R * 0.45, r: R * range(rng, 0.52, 0.64) },
  ];
  foliage(ctx, circles, t.color, t);
  if (!t.accent) return;
  const fruit = pointsIn(circles, 6, t.seed);
  ctx.beginPath();
  for (const p of fruit) circlePath(ctx, p.x, p.y, h * 0.028);
  ctx.fillStyle = t.accent;
  ctx.fill();
  strokeRim(ctx, rimOf(t.accent), t.size, 0.6);
  ctx.beginPath();
  for (const p of fruit) circlePath(ctx, p.x - h * 0.009, p.y - h * 0.009, h * 0.008);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
  ctx.fill();
}

function pineTree(ctx: Ctx, t: Tree): void {
  const { h } = t;
  ctx.beginPath();
  ctx.rect(-h * 0.04, -h * 0.2, h * 0.08, h * 0.2);
  ctx.fillStyle = bodyGradient(ctx, t.trunk, -h * 0.2, 0);
  ctx.fill();
  strokeRim(ctx, rimOf(t.trunk), t.size);
  const tiers = 4;
  for (let k = 0; k < tiers; k++) {
    const yb = -h * (0.14 + k * 0.19);
    const yt = yb - h * (0.36 - k * 0.02);
    const w = h * (0.34 - k * 0.065);
    const tier = () => {
      ctx.beginPath();
      ctx.moveTo(-w, yb);
      ctx.quadraticCurveTo(-w * 0.45, yb - (yb - yt) * 0.42, -w * 0.08, yt + h * 0.012);
      ctx.quadraticCurveTo(0, yt - h * 0.012, w * 0.08, yt + h * 0.012);
      ctx.quadraticCurveTo(w * 0.45, yb - (yb - yt) * 0.42, w, yb);
      ctx.quadraticCurveTo(0, yb + h * 0.06, -w, yb);
      ctx.closePath();
    };
    tier();
    ctx.fillStyle = bodyGradient(ctx, t.color, yt, yb, 0.12, -0.12);
    ctx.fill();
    ctx.save();
    ctx.clip();
    // Sunlit left half.
    ctx.beginPath();
    ctx.moveTo(0, yt);
    ctx.lineTo(-w * 1.1, yb + h * 0.06);
    ctx.lineTo(-w * 0.1, yb + h * 0.06);
    ctx.closePath();
    ctx.fillStyle = rgba(shade(t.color, 0.3), 0.35);
    ctx.fill();
    if (t.accent) {
      // Snow resting on the tier: a cap with a scalloped lower edge.
      const sy = yt + (yb - yt) * 0.42;
      ctx.beginPath();
      ctx.moveTo(-w, sy);
      const scallops = 4;
      for (let s = 0; s < scallops; s++) {
        const x0 = -w + (2 * w * s) / scallops;
        const x1 = x0 + (2 * w) / scallops;
        ctx.quadraticCurveTo((x0 + x1) / 2, sy + h * 0.05, x1, sy);
      }
      ctx.lineTo(w, yt - h * 0.05);
      ctx.lineTo(-w, yt - h * 0.05);
      ctx.closePath();
      ctx.fillStyle = vGradient(ctx, yt, sy + h * 0.05, [t.accent, shade(t.accent, -0.05)]);
      ctx.fill();
    }
    ctx.restore();
    tier();
    strokeRim(ctx, rimOf(t.color), t.size);
  }
}

function palmTree(ctx: Ctx, t: Tree): void {
  const { h, time } = t;
  const lean = t.flip * (0.55 + 0.45 * hash01(t.seed, 1));
  const top = { x: lean * h * 0.26, y: -h * 0.86 };
  const ctrl = { x: -lean * h * 0.02, y: -h * 0.45 };
  const base = { x: 0, y: 0 };
  const trunkPts = Array.from({ length: 14 }, (_, i) => quadPoint(base, ctrl, top, i / 13));
  ctx.beginPath();
  ribbonPath(ctx, trunkPts, (s) => h * (0.075 - 0.032 * s));
  ctx.fillStyle = bodyGradient(ctx, t.trunk, top.y, 0, 0.12, -0.1);
  ctx.fill();
  strokeRim(ctx, rimOf(t.trunk), t.size);
  // Trunk rings.
  ctx.strokeStyle = rgba(shade(t.trunk, -0.25), 0.7);
  ctx.lineWidth = Math.max(1, h * 0.007);
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (let i = 1; i < 10; i++) {
    const s = i / 10;
    const p = quadPoint(base, ctrl, top, s);
    const q = quadPoint(base, ctrl, top, Math.min(1, s + 0.02));
    const half = h * (0.075 - 0.032 * s) * 0.42;
    const len = Math.hypot(q.x - p.x, q.y - p.y) || 1;
    const nx = -(q.y - p.y) / len;
    const ny = (q.x - p.x) / len;
    ctx.moveTo(p.x - nx * half, p.y - ny * half);
    ctx.quadraticCurveTo(p.x + (q.x - p.x) * 2, p.y + h * 0.012, p.x + nx * half, p.y + ny * half);
  }
  ctx.stroke();

  // Fronds: two drooping ones behind, then a fan in front.
  const up = -Math.PI / 2;
  const back = [up - 1.9, up + 1.9];
  const front = [up - 1.3, up - 0.72, up - 0.18, up + 0.35, up + 0.85, up + 1.35];
  const frond = (a: number, k: number, color: string) => {
    const flutter = time ? 0.06 * Math.sin(time * 1.4 + t.phase + k * 0.9) : 0;
    const ang = a + flutter;
    const L = h * (0.4 + 0.1 * hash01(t.seed + 9, k));
    const droop = L * (0.22 + 0.34 * Math.abs(Math.cos(ang)));
    const c = { x: top.x + Math.cos(ang) * L * 0.6, y: top.y + Math.sin(ang) * L * 0.6 };
    const e = { x: c.x + Math.cos(ang) * L * 0.42, y: c.y + Math.sin(ang) * L * 0.42 + droop };
    const spine = Array.from({ length: 13 }, (_, i) => quadPoint(top, c, e, i / 12));
    ctx.beginPath();
    ribbonPath(ctx, spine, (s) => L * 0.24 * Math.sin(Math.PI * Math.min(1, 0.08 + s * 1.02)) ** 0.8 * (0.74 + 0.26 * Math.abs(Math.sin(s * Math.PI * 6))));
    ctx.fillStyle = color;
    ctx.fill();
    strokeRim(ctx, rimOf(color), t.size, 0.9);
    ctx.beginPath();
    ctx.moveTo(top.x, top.y);
    ctx.quadraticCurveTo(c.x, c.y, e.x, e.y);
    ctx.strokeStyle = rgba(shade(color, 0.35), 0.8);
    ctx.lineWidth = Math.max(1, h * 0.006);
    ctx.stroke();
  };
  back.forEach((a, k) => frond(a, k + 10, shade(t.color, -0.14)));
  if (t.accent) {
    ctx.beginPath();
    for (const [dx, dy] of [
      [-0.035, 0.035],
      [0.03, 0.04],
      [0, 0.06],
    ] as const) {
      circlePath(ctx, top.x + dx * h, top.y + dy * h, h * 0.036);
    }
    ctx.fillStyle = t.accent;
    ctx.fill();
    strokeRim(ctx, rimOf(t.accent), t.size, 0.7);
  }
  front.forEach((a, k) => frond(a, k, t.color));
}

function baobabTree(ctx: Ctx, t: Tree): void {
  const { h, flip: f } = t;
  const W = h * 0.3;
  // Bottle-shaped trunk with stubby root-like branches on top, as one outline.
  const outline: Point[] = [
    { x: -W, y: 0 },
    { x: -W * 1.1, y: -h * 0.22 },
    { x: -W * 0.66, y: -h * 0.56 },
    { x: -W * 1.3 * f, y: -h * 0.8 },
    { x: -W * 1.18 * f, y: -h * 0.86 },
    { x: -W * 0.52, y: -h * 0.7 },
    { x: -W * 0.52, y: -h * 0.94 },
    { x: -W * 0.3, y: -h * 0.96 },
    { x: -W * 0.08, y: -h * 0.74 },
    { x: W * 0.22, y: -h * 0.99 },
    { x: W * 0.42, y: -h * 0.96 },
    { x: W * 0.46, y: -h * 0.72 },
    { x: W * 1.28 * f, y: -h * 0.84 },
    { x: W * 1.3 * f, y: -h * 0.78 },
    { x: W * 0.66, y: -h * 0.56 },
    { x: W * 1.06, y: -h * 0.22 },
    { x: W, y: 0 },
    { x: 0, y: h * 0.02 },
  ];
  const trunkShape = () => {
    ctx.beginPath();
    const n = outline.length;
    const mid = (a: Point, b: Point) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    const start = mid(outline[n - 1]!, outline[0]!);
    ctx.moveTo(start.x, start.y);
    for (let i = 0; i < n; i++) {
      const p = outline[i]!;
      const m = mid(p, outline[(i + 1) % n]!);
      ctx.quadraticCurveTo(p.x, p.y, m.x, m.y);
    }
    ctx.closePath();
  };
  // Leafy tufts behind the branch tips, joined into a broad, flat-topped crown.
  const tips: Circle[] = [
    { x: -W * 1.28 * f, y: -h * 0.86, r: h * 0.09 },
    { x: -W * 0.86, y: -h * 0.95, r: h * 0.075 },
    { x: -W * 0.42, y: -h * 1.0, r: h * 0.085 },
    { x: W * 0.32, y: -h * 1.03, r: h * 0.09 },
    { x: W * 0.84, y: -h * 0.97, r: h * 0.075 },
    { x: W * 1.32 * f, y: -h * 0.87, r: h * 0.085 },
  ];
  const tufts: Circle[] = tips.flatMap((c) => [
    c,
    { x: c.x - c.r * 0.8, y: c.y + c.r * 0.25, r: c.r * 0.7 },
    { x: c.x + c.r * 0.8, y: c.y + c.r * 0.2, r: c.r * 0.72 },
  ]);
  foliage(ctx, tufts, t.color, t);

  trunkShape();
  const g = ctx.createLinearGradient(-W * 1.1, 0, W * 1.1, 0);
  g.addColorStop(0, shade(t.trunk, 0.14));
  g.addColorStop(0.45, t.trunk);
  g.addColorStop(1, shade(t.trunk, -0.16));
  ctx.fillStyle = g;
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.strokeStyle = rgba(shade(t.trunk, -0.22), 0.45);
  ctx.lineWidth = Math.max(1, h * 0.008);
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (const k of [-0.6, -0.2, 0.25, 0.62]) {
    ctx.moveTo(W * k * 1.1, 0);
    ctx.quadraticCurveTo(W * k * 1.35, -h * 0.25, W * k * 0.7, -h * 0.55);
  }
  ctx.stroke();
  ctx.restore();
  trunkShape();
  strokeRim(ctx, rimOf(t.trunk), t.size);
  gloss(ctx, -W * 0.55, -h * 0.3, W * 0.14, h * 0.12, 0.25, 0.1);
}

function acaciaTree(ctx: Ctx, t: Tree): void {
  const { h, flip: f } = t;
  const fork = { x: f * h * 0.05, y: -h * 0.5 };
  limb(ctx, { x: 0, y: 0 }, { x: -f * h * 0.03, y: -h * 0.25 }, fork, h * 0.07, h * 0.05, t.trunk, t, false);
  limb(ctx, fork, { x: fork.x - f * h * 0.08, y: -h * 0.62 }, { x: -f * h * 0.3, y: -h * 0.76 }, h * 0.045, h * 0.022, t.trunk, t, false);
  limb(ctx, fork, { x: fork.x + f * h * 0.14, y: -h * 0.6 }, { x: f * h * 0.32, y: -h * 0.78 }, h * 0.045, h * 0.022, t.trunk, t, false);
  limb(ctx, fork, { x: fork.x, y: -h * 0.66 }, { x: f * h * 0.04, y: -h * 0.8 }, h * 0.035, h * 0.02, t.trunk, t, false);
  const blobs = [
    { x: 0, y: -h * 0.8, rx: h * 0.56, ry: h * 0.1 },
    { x: -h * 0.3 * f, y: -h * 0.86, rx: h * 0.24, ry: h * 0.085 },
    { x: h * 0.04 * f, y: -h * 0.9, rx: h * 0.27, ry: h * 0.09 },
    { x: h * 0.34 * f, y: -h * 0.85, rx: h * 0.21, ry: h * 0.075 },
  ];
  ellipseBlob(ctx, blobs, bodyGradient(ctx, t.color, -h * 0.99, -h * 0.7, 0.18, -0.16), rimOf(t.color), t.size);
  ctx.beginPath();
  for (const b of blobs.slice(1)) ellipsePath(ctx, b.x - b.rx * 0.15, b.y - b.ry * 0.3, b.rx * 0.6, b.ry * 0.45);
  ctx.fillStyle = rgba(shade(t.color, 0.26), 0.5);
  ctx.fill();
}

function bambooClump(ctx: Ctx, t: Tree): void {
  const { h, time } = t;
  const n = 3 + Math.floor(hash01(t.seed, 2) * 2);
  const seg = h * 0.11;
  const w = h * 0.042;
  for (let s = 0; s < n; s++) {
    const off = s - (n - 1) / 2;
    const H = h * (0.78 + 0.22 * hash01(t.seed + 11, s));
    const x0 = off * h * 0.075;
    const bend = time ? Math.sin(time * 0.8 + t.phase + s * 0.7) * h * 0.02 : 0;
    const topPt = { x: x0 + off * h * 0.06 + bend, y: -H };
    const ctrl = { x: x0 + off * h * 0.01, y: -H * 0.5 };
    const at = (u: number) => quadPoint({ x: x0, y: 0 }, ctrl, topPt, u);
    const pts = Array.from({ length: 10 }, (_, i) => at(i / 9));
    ctx.beginPath();
    ribbonPath(ctx, pts, (u) => w * (1 - 0.25 * u));
    const g = ctx.createLinearGradient(x0 - w, 0, x0 + w, 0);
    g.addColorStop(0, shade(t.trunk, 0.2));
    g.addColorStop(0.5, t.trunk);
    g.addColorStop(1, shade(t.trunk, -0.14));
    ctx.fillStyle = g;
    ctx.fill();
    strokeRim(ctx, rimOf(t.trunk), t.size, 0.9);
    // Nodes.
    const nodes = Math.floor(H / seg);
    ctx.strokeStyle = t.accent ?? shade(t.trunk, -0.3);
    ctx.lineWidth = Math.max(1, h * 0.008);
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (let k = 1; k < nodes; k++) {
      const p = at((k * seg) / H);
      const half = (w * (1 - 0.25 * ((k * seg) / H))) / 2;
      ctx.moveTo(p.x - half, p.y);
      ctx.quadraticCurveTo(p.x, p.y + h * 0.008, p.x + half, p.y);
    }
    ctx.stroke();
    // Leaves sprout from the upper nodes, alternating sides.
    ctx.beginPath();
    for (let k = Math.floor(nodes * 0.4); k <= nodes; k++) {
      const p = at(Math.min(1, (k * seg) / H));
      const side = (k + s) % 2 === 0 ? 1 : -1;
      const flutter = time ? 0.12 * Math.sin(time * 1.3 + k + s) : 0;
      for (const spread of [0.25, 0.75]) {
        const a = side > 0 ? spread + flutter : Math.PI - spread - flutter;
        const len = h * 0.13;
        leafPath(ctx, p.x + Math.cos(a) * len * 0.5, p.y + Math.sin(a) * len * 0.5, len, h * 0.02, a);
      }
    }
    ctx.fillStyle = t.color;
    ctx.fill();
    strokeRim(ctx, rgba(rimOf(t.color), 0.8), t.size, 0.6);
  }
}

function fern(ctx: Ctx, t: Tree): void {
  const { h, time } = t;
  const fronds = 7;
  const order = [0, 6, 1, 5, 2, 4, 3]; // outer (back) fronds first
  for (const k of order) {
    const u = k / (fronds - 1) - 0.5;
    const flutter = time ? 0.05 * Math.sin(time * 1.1 + t.phase + k) : 0;
    const a = -Math.PI / 2 + u * 2.5 + flutter + (hash01(t.seed, k) - 0.5) * 0.15;
    const L = h * (0.78 + 0.22 * hash01(t.seed + 1, k)) * (1 - 0.3 * Math.abs(u));
    const c = { x: Math.cos(a) * L * 0.6, y: Math.sin(a) * L * 0.6 };
    const e = { x: Math.cos(a) * L * 0.95, y: Math.sin(a) * L * 0.9 + L * 0.38 * Math.abs(Math.cos(a)) };
    const o = { x: 0, y: 0 };
    const color = Math.abs(u) > 0.3 ? shade(t.color, -0.12) : t.color;
    ctx.beginPath();
    for (let j = 1; j <= 11; j++) {
      const s = j / 12;
      const p = quadPoint(o, c, e, s);
      const q = quadPoint(o, c, e, Math.min(1, s + 0.02));
      const dir = Math.atan2(q.y - p.y, q.x - p.x);
      const len = L * 0.2 * (1 - s * 0.75);
      for (const side of [-1, 1]) {
        const la = dir + side * 0.95;
        leafPath(ctx, p.x + Math.cos(la) * len * 0.5, p.y + Math.sin(la) * len * 0.5, len, len * 0.22, la);
      }
    }
    ctx.fillStyle = color;
    ctx.fill();
    strokeRim(ctx, rgba(rimOf(color), 0.7), t.size, 0.55);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.quadraticCurveTo(c.x, c.y, e.x, e.y);
    ctx.strokeStyle = t.trunk;
    ctx.lineWidth = Math.max(1, h * 0.012);
    ctx.lineCap = 'round';
    ctx.stroke();
  }
}

/** A king protea: a cup of pointed pink bracts around a pale, fuzzy dome. */
function proteaHead(ctx: Ctx, x: number, y: number, r: number, accent: string, t: Tree): void {
  const petal = (a: number, len: number, width: number, color: string) => {
    const bx = x + Math.cos(a) * len * 0.5;
    const by = y + r * 0.3 + Math.sin(a) * len * 0.5;
    ctx.beginPath();
    leafPath(ctx, bx, by, len, width, a);
    ctx.fillStyle = vGradient(ctx, y - r, y + r * 0.4, [shade(color, 0.35), color]);
    ctx.fill();
    strokeRim(ctx, rimOf(color), t.size, 0.6);
  };
  const up = -Math.PI / 2;
  for (let k = 0; k < 7; k++) petal(up + (k / 6 - 0.5) * 2.2, r * 1.35, r * 0.3, shade(accent, -0.12));
  ctx.beginPath();
  ellipsePath(ctx, x, y - r * 0.08, r * 0.5, r * 0.44);
  ctx.fillStyle = vGradient(ctx, y - r * 0.5, y + r * 0.35, ['#fffaf5', shade(accent, 0.62)]);
  ctx.fill();
  strokeRim(ctx, rgba(rimOf(accent), 0.6), t.size, 0.6);
  // Fuzzy texture on the dome.
  ctx.beginPath();
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    circlePath(ctx, x + Math.cos(a) * r * 0.28, y - r * 0.1 + Math.sin(a) * r * 0.22, r * 0.045);
  }
  ctx.fillStyle = rgba(accent, 0.45);
  ctx.fill();
  for (let k = 0; k < 4; k++) petal(up + (k / 3 - 0.5) * 2.6, r * 0.95, r * 0.26, accent);
}

function proteaBush(ctx: Ctx, t: Tree): void {
  const { h, time } = t;
  const bush: Circle[] = [
    { x: 0, y: -h * 0.28, r: h * 0.27 },
    { x: -h * 0.27, y: -h * 0.18, r: h * 0.19 },
    { x: h * 0.28, y: -h * 0.19, r: h * 0.2 },
    { x: -h * 0.12, y: -h * 0.44, r: h * 0.17 },
    { x: h * 0.15, y: -h * 0.43, r: h * 0.16 },
  ];
  foliage(ctx, bush, t.color, t);
  // Narrow fynbos leaves across the bush.
  ctx.beginPath();
  const rng = rngFor(t.seed, 3);
  for (let i = 0; i < 14; i++) {
    const c = bush[i % bush.length]!;
    const a = range(rng, -2.6, -0.5);
    leafPath(ctx, c.x + range(rng, -0.6, 0.6) * c.r, c.y + range(rng, -0.4, 0.6) * c.r, h * 0.1, h * 0.014, a);
  }
  ctx.fillStyle = rgba(shade(t.color, 0.25), 0.8);
  ctx.fill();
  const accent = t.accent ?? '#ff85a8';
  const heads: [number, number, number][] = [
    [0, -h * 0.6, 1],
    [-h * 0.3, -h * 0.4, 0.72],
    [h * 0.32, -h * 0.42, 0.8],
  ];
  const count = 2 + Math.floor(hash01(t.seed, 4) * 2);
  heads.slice(0, count).forEach(([hx, hy, k], i) => {
    const nod = time ? 0.08 * Math.sin(time * 1.2 + t.phase + i * 1.7) : 0;
    ctx.save();
    ctx.translate(hx, hy + h * 0.16);
    ctx.rotate(nod);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, -h * 0.14);
    ctx.strokeStyle = t.trunk;
    ctx.lineWidth = Math.max(1.5, h * 0.02);
    ctx.lineCap = 'round';
    ctx.stroke();
    proteaHead(ctx, 0, -h * 0.16, h * 0.15 * k, accent, t);
    ctx.restore();
  });
}

function mushroom(ctx: Ctx, t: Tree): void {
  const { h } = t;
  const stalk = [
    { x: 0, y: 0 },
    { x: 0, y: -h * 0.28 },
    { x: 0, y: -h * 0.56 },
  ];
  ctx.beginPath();
  ribbonPath(ctx, stalk, (s) => h * (0.22 - 0.08 * s + 0.03 * Math.sin(Math.PI * s)));
  const sg = ctx.createLinearGradient(-h * 0.12, 0, h * 0.12, 0);
  sg.addColorStop(0, shade(t.trunk, 0.1));
  sg.addColorStop(0.6, t.trunk);
  sg.addColorStop(1, shade(t.trunk, -0.12));
  ctx.fillStyle = sg;
  ctx.fill();
  strokeRim(ctx, shade(t.trunk, -0.3), t.size);
  const R = h * 0.46;
  const yb = -h * 0.5;
  ctx.beginPath();
  ellipsePath(ctx, 0, yb + h * 0.03, R * 0.82, h * 0.055);
  ctx.fillStyle = shade(t.trunk, -0.14);
  ctx.fill();
  const cap = () => {
    ctx.beginPath();
    ctx.moveTo(-R, yb);
    ctx.bezierCurveTo(-R * 1.05, -h * 1.17, R * 1.05, -h * 1.17, R, yb);
    ctx.quadraticCurveTo(0, yb + h * 0.09, -R, yb);
    ctx.closePath();
  };
  cap();
  ctx.fillStyle = bodyGradient(ctx, t.color, -h, yb, 0.16, -0.12);
  ctx.fill();
  if (t.accent) {
    ctx.save();
    ctx.clip();
    ctx.beginPath();
    const spots: [number, number, number][] = [
      [-0.5, -0.74, 0.17],
      [0.12, -0.9, 0.2],
      [0.56, -0.66, 0.14],
      [-0.12, -0.62, 0.1],
      [0.34, -0.55, 0.09],
      [-0.88, -0.56, 0.1],
    ];
    for (const [sx, sy, sr] of spots) ellipsePath(ctx, sx * R, sy * h, sr * R, sr * R * 0.85);
    ctx.fillStyle = t.accent;
    ctx.fill();
    ctx.restore();
  }
  cap();
  strokeRim(ctx, rimOf(t.color), t.size);
  gloss(ctx, -R * 0.42, -h * 0.86, R * 0.26, R * 0.12, 0.5);
}

function lollipop(ctx: Ctx, t: Tree): void {
  const { h } = t;
  ctx.beginPath();
  ribbonPath(
    ctx,
    [
      { x: 0, y: 0 },
      { x: 0, y: -h * 0.62 },
    ],
    () => h * 0.036,
  );
  ctx.fillStyle = t.trunk;
  ctx.fill();
  strokeRim(ctx, shade(t.trunk, -0.25), t.size, 0.8);
  const r = h * 0.22;
  const cy = -h * 0.77;
  ctx.beginPath();
  circlePath(ctx, 0, cy, r);
  const g = ctx.createRadialGradient(-r * 0.3, cy - r * 0.3, r * 0.1, 0, cy, r);
  g.addColorStop(0, shade(t.color, 0.3));
  g.addColorStop(1, t.color);
  ctx.fillStyle = g;
  ctx.fill();
  ctx.save();
  ctx.clip();
  // Swirl: an Archimedean spiral in the accent colour.
  const turns = 2.6;
  const rot = hash01(t.seed, 6) * Math.PI * 2;
  ctx.beginPath();
  for (let i = 0; i <= 80; i++) {
    const s = i / 80;
    const a = rot + s * turns * Math.PI * 2;
    const rr = r * 1.05 * s;
    const px = Math.cos(a) * rr;
    const py = cy + Math.sin(a) * rr;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.strokeStyle = t.accent ?? '#ffffff';
  ctx.lineWidth = r * 0.17;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.restore();
  ctx.beginPath();
  circlePath(ctx, 0, cy, r);
  strokeRim(ctx, rimOf(t.color), t.size);
  gloss(ctx, -r * 0.38, cy - r * 0.42, r * 0.3, r * 0.14, 0.6);
}

function jungleTree(ctx: Ctx, t: Tree): void {
  const { h, flip: f } = t;
  // Buttress roots.
  for (const side of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(side * h * 0.02, -h * 0.16);
    ctx.quadraticCurveTo(side * h * 0.05, -h * 0.03, side * h * 0.13, h * 0.005);
    ctx.lineTo(side * h * 0.01, h * 0.005);
    ctx.closePath();
    ctx.fillStyle = shade(t.trunk, -0.06);
    ctx.fill();
    strokeRim(ctx, rimOf(t.trunk), t.size);
  }
  limb(ctx, { x: 0, y: 0 }, { x: -f * h * 0.03, y: -h * 0.35 }, { x: f * h * 0.04, y: -h * 0.66 }, h * 0.1, h * 0.06, t.trunk, t);
  // Lianas hanging from the canopy.
  ctx.strokeStyle = shade(t.color, -0.35);
  ctx.lineWidth = Math.max(1, h * 0.008);
  ctx.beginPath();
  for (const k of [-0.22, 0.18]) {
    ctx.moveTo(h * k, -h * 0.66);
    ctx.quadraticCurveTo(h * (k + 0.03), -h * 0.45, h * (k - 0.01), -h * 0.32);
  }
  ctx.stroke();
  const circles: Circle[] = [
    { x: -h * 0.3, y: -h * 0.66, r: h * 0.14 },
    { x: -h * 0.14, y: -h * 0.7, r: h * 0.16 },
    { x: h * 0.04, y: -h * 0.68, r: h * 0.17 },
    { x: h * 0.22, y: -h * 0.69, r: h * 0.16 },
    { x: h * 0.36, y: -h * 0.65, r: h * 0.12 },
    { x: -h * 0.17, y: -h * 0.84, r: h * 0.15 },
    { x: h * 0.12, y: -h * 0.86, r: h * 0.16 },
    { x: -h * 0.02, y: -h * 0.92, r: h * 0.12 },
  ];
  foliage(ctx, circles, t.color, t);
  if (t.accent) tinyFlowers(ctx, pointsIn(circles, 7, t.seed), h * 0.022, t.accent);
}

function blossomTree(ctx: Ctx, t: Tree): void {
  const { h, flip: f } = t;
  const fork = { x: f * h * 0.05, y: -h * 0.48 };
  limb(ctx, { x: 0, y: 0 }, { x: -f * h * 0.06, y: -h * 0.25 }, fork, h * 0.09, h * 0.055, t.trunk, t);
  limb(ctx, fork, { x: fork.x - f * h * 0.1, y: -h * 0.56 }, { x: -f * h * 0.24, y: -h * 0.7 }, h * 0.045, h * 0.02, t.trunk, t, false);
  limb(ctx, fork, { x: fork.x + f * h * 0.12, y: -h * 0.55 }, { x: f * h * 0.28, y: -h * 0.68 }, h * 0.045, h * 0.02, t.trunk, t, false);
  const rng = rngFor(t.seed, 8);
  const circles: Circle[] = [
    { x: 0, y: -h * 0.74, r: h * 0.2 },
    { x: -h * 0.26, y: -h * 0.66, r: h * range(rng, 0.13, 0.16) },
    { x: h * 0.28, y: -h * 0.65, r: h * range(rng, 0.13, 0.16) },
    { x: -h * 0.16, y: -h * 0.86, r: h * range(rng, 0.13, 0.15) },
    { x: h * 0.16, y: -h * 0.87, r: h * range(rng, 0.13, 0.15) },
    { x: -h * 0.38, y: -h * 0.56, r: h * 0.09 },
    { x: h * 0.4, y: -h * 0.56, r: h * 0.09 },
  ];
  foliage(ctx, circles, t.color, t);
  tinyFlowers(ctx, pointsIn(circles, 14, t.seed), h * 0.02, t.accent ?? '#ffffff');
}

const SPECIES: Record<TreeSpecies, (ctx: Ctx, t: Tree) => void> = {
  round: roundTree,
  pine: pineTree,
  palm: palmTree,
  baobab: baobabTree,
  acacia: acaciaTree,
  bamboo: bambooClump,
  fern,
  protea: proteaBush,
  mushroom,
  lollipop,
  jungle: jungleTree,
  blossom: blossomTree,
};

/** How far (radians) each species bends in the breeze when `sway` is on. */
const SWAY: Record<TreeSpecies, number> = {
  round: 0.015,
  pine: 0.012,
  palm: 0.018,
  baobab: 0.006,
  acacia: 0.012,
  bamboo: 0.03,
  fern: 0.035,
  protea: 0.02,
  mushroom: 0.01,
  lollipop: 0.015,
  jungle: 0.012,
  blossom: 0.018,
};

function drawTrees(ctx: Ctx, layer: TreesLayer, env: PaintEnv, time: number): void {
  const paint = SPECIES[layer.species];
  for (const plan of planTrees(layer, env)) {
    const angle = time ? SWAY[layer.species] * Math.sin(time * 0.9 + plan.phase) : 0;
    ctx.save();
    ctx.translate(plan.x, plan.y);
    if (angle) ctx.rotate(angle);
    paint(ctx, {
      h: plan.h,
      color: layer.color,
      trunk: layer.trunk,
      accent: layer.accent,
      size: env.size,
      seed: plan.seed,
      time,
      phase: plan.phase,
      flip: plan.flip,
    });
    ctx.restore();
  }
}

export const treesPainter: Painter<TreesLayer> = {
  extent: (layer, { size }) => ({
    top: layer.baseline * size.height - layer.size * size.unit * 1.45,
    bottom: layer.baseline * size.height + size.unit * 0.03,
  }),
  still(ctx, layer, env) {
    if (!layer.sway) drawTrees(ctx, layer, env, 0);
  },
  moving(ctx, layer, env) {
    // Swaying trees are drawn whole every frame; the time-0 frame is their static pose.
    if (layer.sway) drawTrees(ctx, layer, env, env.time);
  },
  animated: (layer) => layer.sway === true,
};
